/**
 * A Turnstile token for a submission with no form on screen.
 *
 * Every deployed jobsite has a per-site widget, and the lead and OTP endpoints
 * reject a submission without a token — but the visible widgets belong to
 * ContactForm and BookingWidget, which an agent's page may not have mounted.
 *
 * Rendered `interaction-only`: invisible where a managed widget clears
 * silently, visible where Cloudflare wants a click. Hidden, it would never
 * produce a token on those visits.
 */

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const DEFAULT_TIMEOUT_MS = 20_000;

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      appearance?: string;
      callback?: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
}

function turnstileApi(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

/** Load Cloudflare's script if the page hasn't already (SSG builds inject it). */
function ensureScript(): void {
  if (document.querySelector('script[src*="turnstile"]')) return;
  const script = document.createElement("script");
  script.src = TURNSTILE_SCRIPT_URL;
  script.async = true;
  document.head.appendChild(script);
}

/** Resolves with a token, or null when the challenge goes unsolved in time. */
export function requestTurnstileToken(
  siteKey: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<string | null> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const container = document.createElement("div");
    container.setAttribute("data-servicepro-webmcp-turnstile", "");
    container.style.cssText =
      "position:fixed;right:16px;bottom:16px;z-index:2147483647;display:flex;justify-content:flex-end";
    document.body.appendChild(container);

    let widgetId: string | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      if (poll) clearInterval(poll);
      if (timer) clearTimeout(timer);
      if (widgetId !== undefined) {
        try {
          turnstileApi()?.remove(widgetId);
        } catch {
          /* widget already gone */
        }
      }
      container.remove();
      resolve(token);
    };

    timer = setTimeout(() => finish(null), timeoutMs);

    const render = () => {
      const api = turnstileApi();
      if (widgetId !== undefined || !api) return;
      try {
        widgetId = api.render(container, {
          sitekey: siteKey,
          appearance: "interaction-only",
          callback: (token: string) => finish(token),
          "error-callback": () => finish(null),
          "expired-callback": () => finish(null),
        });
      } catch {
        finish(null);
      }
    };

    if (turnstileApi()) {
      render();
    } else {
      ensureScript();
      poll = setInterval(() => {
        if (turnstileApi()) {
          clearInterval(poll);
          render();
        }
      }, 50);
    }
  });
}
